import { ReactNode, useCallback, useMemo, useState } from 'react';
import copy from 'copy-to-clipboard';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { ChainIcon, useModal, useShortenURL, BASE_URL } from '@restworld/utils-common';

import { ShareIcon, ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { Button, Heading, Modal, ModalContent } from '@restworld/ui-ds-base';

/* eslint-disable-next-line */
export interface AmbassadorButtonProps {
  url: string;
  regenerateURL: () => Promise<void>;
  type: 'error' | 'success';
  loading?: boolean;
  className?: string;
  /**
   * Render the icon without its label. Used where the button shares a row with
   * other CTAs and the labels do not fit, e.g. the explorer filters bar on
   * mobile for a user who is both a worker and an ambassador.
   */
  iconOnly?: boolean;
}
export const AmbassadorButton = ({ url, type, regenerateURL, loading, className, iconOnly }: AmbassadorButtonProps) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const { show, open, close } = useModal();
  const copyLink = useCallback(() => {
    copy(url);
    setIsCopied(true);
  }, [url]);
  const isSuccess = useMemo(() => type === 'success', [type]);

  return (
    <>
      <Button
        type="button"
        StartIcon={ShareIcon}
        onClick={async () => {
          await regenerateURL();
          open();
        }}
        loading={loading}
        className={className}
        aria-label={t('common:share')}
      >
        {iconOnly ? null : t('common:share')}
      </Button>
      <PopupModalMessage
        show={show}
        close={() => {
          close();
          setIsCopied(false);
        }}
        headIcon={<ChainIcon stroke={isSuccess ? '#007740' : '#cc0022'} />}
        title={
          <Heading size={2} weight="bold" className="leading-9">
            {isSuccess ? t('common:link_created_successfully') : t('job-explorer-page:share_this_job_title')}
          </Heading>
        }
        subTitle={
          <div onClick={copyLink} className="cursor-pointer">
            <Heading
              as="span"
              size={4}
              weight="normal"
              color="muted-foreground"
              className="leading-6 inline-flex items-center gap-1"
            >
              {!isSuccess ? t('job-explorer-page:try_again_to_generate_a_new_link') : url}
              {isCopied ? (
                <ClipboardDocumentCheckIcon className="h-5 w-5 text-green-600" />
              ) : isSuccess ? (
                <ClipboardDocumentIcon className="h-5 w-5" />
              ) : null}
            </Heading>
          </div>
        }
        submitButton={
          <Button
            loading={loading}
            fullwidth
            variant="primary"
            onClick={() => {
              if (isSuccess) {
                copyLink();
              } else regenerateURL();
            }}
          >
            {isCopied ? t('common:link_copied') : !isSuccess ? t('common:recreate_link') : t('common:copy_link')}
          </Button>
        }
      />
    </>
  );
};

export default AmbassadorButton;
const PopupModalMessage = ({
  show,
  close,
  subTitle,
  title,
  submitButton,
  headIcon,
}: {
  show: boolean;
  close: () => void;
  subTitle?: ReactNode;
  title: ReactNode;
  submitButton?: ReactNode;
  headIcon?: ReactNode;
}) => {
  return (
    <Modal open={show} onOpenChange={(open) => !open && close()}>
      <ModalContent size="lg" hideHeader>
        <div className="flex items-center justify-center flex-col gap-8 w-full text-center">
          {headIcon}
          <div className="flex flex-col gap-4 items-center">
            {title}
            {subTitle}
          </div>
          {submitButton}
        </div>
      </ModalContent>
    </Modal>
  );
};
export const useShortUrl = () => {
  const [loading, setLoading] = useState(false);
  const [shortUrl, setShortUrl] = useState('');
  const { getShortenURL } = useShortenURL();
  const { query } = useRouter();
  const callApi = useCallback(
    async (refCode: string) => {
      setLoading(true);
      const urlQueryParams = generateQueryString({
        ...query,
        utm_source: 'referral',
        utm_medium: 'ambassador',
        utm_campaign: 'worker_acquisition',
        ref_code: refCode,
      });
      const longURL = `${BASE_URL}/cerco-lavoro${urlQueryParams}`;
      const urlResponse: string = await getShortenURL({
        longURL,
      });
      setShortUrl(urlResponse);
      setLoading(false);
    },
    [getShortenURL, query]
  );
  return { shortUrl, loading, getShortenURL: callApi };
};
const generateQueryString = (query: { [x: string]: string }): string => {
  const queryString = Object.keys(query)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
    .join('&');
  return `?${queryString}`;
};
